import { DEFAULT_CDN_URL } from './constants';
import { injectMacros, injectMacrosText } from './utils/macros';
import { createDebugLogger } from './utils/debug';
const debug = createDebugLogger('utils');
/**
 * Replaces placeholders like {{key}} in a string with values from an object.
 */
export function interpolateStr(str, values) {
    if (!str)
        return '';
    // Match {placeholder} or {placeholder|defaultValue}
    return str.replace(/\{([^\}]+)\}/g, (_, key) => {
        const parts = key.split('|');
        const k = parts[0].trim();
        const defaultValue = parts[1]?.trim() || ''; // Use empty string if no default value part
        return values.hasOwnProperty(k) ? values[k] : defaultValue;
    });
}
export { injectMacros, injectMacrosText, macroValueIsFalsy, expandCouDescriptionMacrosToRawHtml, collectRawMacroKeys, } from './utils/macros';
export { withCreativeTypeImageMacros, withPublisherImageMacros, } from './utils/campaignImageMacros';
export { getAdpxAnimateAttribute, getViewportWidth, hasAdEntranceAnimation, } from './utils/adAnimation';
/**
 * Appends a cache-busting timestamp to a URL.
 */
export function applyCacheBuster(url) {
    if (typeof url !== 'string' || !url)
        return '';
    let newUrl = url;
    const templates = [
        '{CBUSTER}',
        '{cbuster}',
        '{TIMESTAMP}',
        '{timestamp}',
        '{CACHEBUSTER}',
        '{cachebuster}',
        '{RANDOM}',
        '{random}',
    ];
    templates.forEach(template => {
        const tencoded = encodeURIComponent(template);
        const tstamp = new Date().valueOf();
        newUrl = newUrl
            .replace(tencoded, tstamp.toString())
            .replace(template, tstamp.toString());
    });
    return newUrl;
}
/**
 * Debounces a function, delaying its execution until after a specified wait time
 * has elapsed since the last time it was invoked.
 */
export function debounce(fn, delay = 300) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}
/**
 * Checks if an HTML string contains meaningful text content.
 * @param htmlString - The HTML string to check.
 * @returns true if contains meaningful text, false otherwise.
 */
export const hasTextContent = (htmlString) => {
    if (!htmlString)
        return false;
    const element = document.createElement('span');
    element.innerHTML = htmlString;
    const text = element.innerText;
    return text?.trim().length > 0;
};
/**
 * Resolves a raw text/HTML field into display-ready HTML: injects {{macros}} when
 * the field has meaningful content, otherwise returns '' so callers can skip rendering.
 */
export const resolveDisplayHtml = (raw, macroAttrs) => (raw && hasTextContent(raw) ? injectMacros(raw, macroAttrs) : '');
/**
 * Text-node counterpart of `resolveDisplayHtml`, for fields rendered as a JSX text
 * child. Macro values are left unescaped so the character itself reaches the DOM.
 */
export const resolveDisplayText = (raw, macroAttrs) => raw && hasTextContent(raw) ? injectMacrosText(raw, macroAttrs) : '';
/**
 * Emptiness check for text-mode output. Unlike `hasTextContent` it never parses the
 * string as HTML — the value is destined for a text node, not markup.
 */
export const hasVisibleText = (text) => !!text && text.trim().length > 0;
/**
 * Adds font imports to the document head.
 * Supports Google Fonts and custom font-face for ATT Aleck Sans and Precision Sans.
 */
export const FONT_CONFIG = {
    'att aleck sans': {
        family: 'ATT Aleck Sans',
        format: 'woff2',
        weights: {
            400: '/fonts/ATT-Aleck-Sans/ATTAleckSans_W_Rg.woff2',
            500: '/fonts/ATT-Aleck-Sans/ATTAleckSans_W_Bd.woff2',
        },
    },
    'precision-sans': {
        family: 'precision-sans',
        format: 'woff',
        weights: {
            400: '/fonts/Precision-Sans/PrecisionSans_W_Rg.woff',
            500: '/fonts/Precision-Sans/PrecisionSans_W_Bd.woff',
        },
    },
    ctct: {
        family: 'CTCT',
        format: 'woff2',
        weights: {
            400: '/fonts/CTCT/CTCTDisplay-Regular.woff2',
            500: '/fonts/CTCT/CTCTDisplay-Medium.woff2',
            600: '/fonts/CTCT/CTCTDisplay-SemiBold.woff2',
            700: '/fonts/CTCT/CTCTDisplay-Bold.woff2',
        },
    },
};
export const addFontImport = (fontFamily) => {
    if (!fontFamily)
        return;
    const head = document.head;
    const url = DEFAULT_CDN_URL;
    const key = fontFamily.toLowerCase();
    const font = FONT_CONFIG[key];
    if (font) {
        const style = document.createElement('style');
        const css = Object.entries(font.weights)
            .map(([weight, path]) => `
        @font-face {
          font-family: '${font.family}';
          src: url('${url}${path}')${font.format ? ` format('${font.format}')` : ''};
          font-weight: ${weight};
          font-display: swap;
        }`)
            .join('');
        style.textContent = css;
        head.appendChild(style);
        return;
    }
    // Google Fonts fallback
    const formattedFont = fontFamily
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    const fontWeights = ['300', '400', '600', '700', '800'].join(';');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${formattedFont.replace(/ /g, '+')}:wght@${fontWeights}&display=swap`;
    head.appendChild(link);
};
/**
 * Helper to fetch and append a static CSS file to the document head.
 * @param cssUrl URL of the CSS file.
 * @param onLoadCallback Optional callback to execute when the stylesheet loads.
 */
export const fetchStaticStylesheet = (cssUrl, onLoadCallback) => {
    // Check if already loaded
    const existingLink = document.querySelector(`link[href='${cssUrl}']`);
    if (existingLink) {
        debug.log(() => `Stylesheet already loaded: ${cssUrl}`);
        return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.addEventListener('load', () => {
        debug.log(() => `Stylesheet loaded: ${cssUrl}`);
        if (onLoadCallback) {
            onLoadCallback();
        }
    });
    link.addEventListener('error', () => {
        debug.error(`Failed to load stylesheet: ${cssUrl}`);
        // Fallback: try to load from local development server for SOU.css
        if (cssUrl.includes('/SOU.css') && !cssUrl.startsWith('/')) {
            const localUrl = '/SOU.css';
            const localLink = document.createElement('link');
            localLink.rel = 'stylesheet';
            localLink.href = localUrl;
            localLink.addEventListener('load', () => {
                debug.log(() => `Local stylesheet loaded: ${localUrl}`);
                if (onLoadCallback) {
                    onLoadCallback();
                }
            });
            localLink.addEventListener('error', () => {
                debug.error(`Failed to load local stylesheet: ${localUrl}`);
            });
            document.head.appendChild(localLink);
        }
    });
    document.head.appendChild(link);
};
/**
 * Injects shimmer animation CSS styles into the document head.
 * This is needed for the shimmer effect on CTA buttons.
 */
export const injectShimmerCSS = () => {
    // Check if shimmer CSS is already injected
    if (document.getElementById('adpx-shimmer-styles')) {
        return;
    }
    const style = document.createElement('style');
    style.id = 'adpx-shimmer-styles';
    style.textContent = `
    @keyframes shimmer {
      0% {
        left: -100%;
      }
      100% {
        left: 100%;
      }
    }

    .adpx_btn-shimmer {
      position: relative;
      overflow: hidden;
    }

    .adpx_btn-shimmer-anim::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
      animation: shimmer 2s infinite;
    }
  `;
    document.head.appendChild(style);
};
export function fireBeacon(eventName, offer) {
    if (offer?.beacons?.[eventName]) {
        try {
            if (navigator.sendBeacon) {
                navigator.sendBeacon(offer.beacons[eventName]);
            }
            else {
                // Fallback for browsers that don't support sendBeacon
                const img = new Image();
                img.src = offer.beacons[eventName];
            }
        }
        catch (e) {
            debug.warn(`Error firing beacon for ${eventName}`, e);
        }
    }
}
export function prefetchImages(imageUrls) {
    if (!imageUrls || !Array.isArray(imageUrls)) {
        return;
    }
    imageUrls.forEach(url => {
        if (url && typeof url === 'string') {
            const img = new Image();
            img.src = url;
        }
    });
}
export function isInWebView() {
    const userAgent = navigator.userAgent;
    // More robust checks might be needed, these are from launcher.js
    const androidWebView = /(wv|WebView)/.test(userAgent); // Simplified Android check, original was complex
    const iOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/.test(userAgent);
    const electronApp = /Electron/.test(userAgent);
    return androidWebView || iOSWebView || electronApp;
}
